import { useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useConfig } from "./hooks/useConfig";
import ErrorScreen from "./screen/ErrorScreen";
import LoadingScreen from "./screen/LoadingScreen";
import Folding3D from "./widgets/Folding3D";
import Guillotine3D from "./widgets/Guillotine3D";
import Sliding3D from "./widgets/Sliding3D";
import Fence3D from "./widgets/Fence3D";
import CompanyLogo from "./components/CompanyLogo";
import DealerLogo from "./components/DealerLogo";
import LeftPanel from "./panels/LeftPanel";
import * as THREE from "three";
import ARModelViewer from "./components/ARModelViewer";
import { handleARProcess } from "./utils/ARHandler";
import { GlassRoof3D } from "./widgets/GlassRoof3D";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function App() {
  const location = useLocation();
  const query = useQuery();
  const encryptedData = query.get("data");
  const [params, setParams] = useState(null);
  const [decryptError, setDecryptError] = useState(null);
  const [isResolvingTinyUrl, setIsResolvingTinyUrl] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const controlsRef = useRef();
  const rendererRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const modelRef = useRef();

  const [aluminumColor, setAluminumColor] = useState("#A5A5A5");
  const [glassColor, setGlassColor] = useState("#E7DFCF");
  const [glassColor2, setGlassColor2] = useState("#E7DFCF");

  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isARLoading, setIsARLoading] = useState(false);
  const [arUrls, setArUrls] = useState({ glb: null, usdz: null });

  const modelViewerRef = useRef();
  const { config, error } = useConfig(params);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const timeoutRef = useRef(null);

  // URL'deki olası tiny_url kodunu tespit et (örn: /XpYGsZ veya ?tiny_url=XpYGsZ)
  const getTinyCode = () => {
    const queryTiny = query.get("tiny_url") || query.get("tiny");
    if (queryTiny) return queryTiny.trim();

    const cleanPath = (location.pathname || "").replace(/^\/+|\/+$/g, "");
    if (!cleanPath) return null;

    const isStaticFile = /\.(png|jpg|jpeg|svg|ico|json|js|css|glb|usdz|html)$/i.test(cleanPath);
    if (isStaticFile) return null;

    const segments = cleanPath.split("/").filter(Boolean);
    if (segments.length === 1) {
      return segments[0];
    }
    if (segments.length === 2 && segments[0].toLowerCase() === "tiny") {
      return segments[1];
    }
    return null;
  };

  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent;
      const isMobileDevice =
        /Mobi|Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
  }, []);

  useEffect(() => {
    // 1. Şifrelenmiş veri varsa doğrudan çöz
    if (encryptedData) {
      try {
        const key = CryptoJS.enc.Utf8.parse("CamMetalApp2025threeDview7654321");
        const iv = CryptoJS.enc.Utf8.parse("0000000000000000");
        const decrypted = CryptoJS.AES.decrypt(decodeURIComponent(encryptedData), key, {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });
        const plainText = decrypted.toString(CryptoJS.enc.Utf8);
        const parsed = JSON.parse(plainText);

        // 🔍 Tarih kontrolü
        const parts = parsed.date.split("/");
        const linkDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // yyyy-MM-dd
        const today = new Date();
        linkDate.setHours(0, 0, 0, 0); // sadece gün karşılaştırması
        today.setHours(0, 0, 0, 0); // sadece gün karşılaştırması

        if (linkDate < today) {
          setDecryptError("Link kullanım süresi dolmuştur");
          return;
        }
        setIsEditMode(parsed.isEdit === true);
        setParams(parsed);
      } catch (err) {
        setDecryptError("Veri çözülemedi. Lütfen bağlantıyı kontrol edin.");
      }
      return;
    }

    // 2. encryptedData yoksa: tiny_url kontrolü yap ve servisten uzun URL'yi al
    const tinyCode = getTinyCode();
    if (tinyCode) {
      setIsResolvingTinyUrl(true);
      const fetchLongUrl = async () => {
        try {
          const url = `https://api.albertgenauapp.com/longurl?tiny_url=${encodeURIComponent(tinyCode)}`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Auth-Key": "sbak",
              "Client-Service": "sbak-frontend-client",
            },
          });

          const json = await response.json();

          if (json?.status?.code === 200 && json?.result) {
            // Başarılı: Gelen uzun URL'ye yönlendir
            window.location.replace(json.result);
          } else {
            setDecryptError(json?.status?.message || "Bağlantı bulunamadı veya süresi dolmuş.");
            setIsResolvingTinyUrl(false);
          }
        } catch (err) {
          console.error("Long URL çözümlenirken hata oluştu:", err);
          setDecryptError("Bağlantı yönlendirilirken sunucu hatası oluştu.");
          setIsResolvingTinyUrl(false);
        }
      };

      fetchLongUrl();
      return;
    }

    // 3. Veri veya geçerli bir tiny_url yoksa hata ver
    setDecryptError("Veri alınamadı! Lütfen linki kontrol edin.");
  }, [encryptedData, location.pathname, location.search]);

  useEffect(() => {
    if (config) {
      if (config.almColor) setAluminumColor(config.almColor);
      if (config.glassColor) setGlassColor(config.glassColor);
      if (config.glassColor2) setGlassColor2(config.glassColor2);
    }
  }, [config]);

  useEffect(() => {
    let frameId;

    const rotate = () => {
      if (!isUserInteracting && modelRef.current) {
        modelRef.current.rotation.y += 0.002;
      }
      frameId = requestAnimationFrame(rotate);
    };

    rotate();

    return () => cancelAnimationFrame(frameId);
  }, [isUserInteracting]);

  const handleARClick = () => {
    try {
      if (window.FlutterBridge && typeof window.FlutterBridge.postMessage === 'function') {
        window.FlutterBridge.postMessage(JSON.stringify({
          action: 'openExternal',
          url: window.location.href
        }));
      }
    } catch (e) {
      console.warn('FlutterBridge gönderim hatası:', e);
    }

    setIsExporting(true);
    setTimeout(() => {
      handleARProcess({
        modelRef,
        setLoading: setIsARLoading,
        setArUrls,
        setIsExporting: setIsExporting,
        params: params
      });
      setIsExporting(false);
    }, 100);
  };

  const handleToggle = () => {
    Object.keys(window.__panelStates || {}).forEach(key => {
      const p = window.__panelStates[key];
      if (isOpen) {
        p.closingIndex = p.isReversed ? 0 : (p.closingIndex ?? 0);
      } else {
        p.activeIndex = p.isReversed ? (p.activeIndex ?? 0) : 0;
      }
    });
    setIsOpen(prev => !prev);
  };

  const renderSystem = () => {
    const cat = config.category;

    if ([2, 3, 11, 12].includes(cat)) {
      return <Folding3D isExporting={isExporting} config={config} isOpenTrigger={isOpen} aluminumColor={aluminumColor} glassColor={glassColor} glassColor2={glassColor2} />;
    }
    if ([6, 7].includes(cat)) return <Guillotine3D isExporting={isExporting} config={config} isOpenTrigger={isOpen} aluminumColor={aluminumColor} glassColor={glassColor} glassColor2={glassColor2} />;
    if ([4, 5].includes(cat)) return <Sliding3D isExporting={isExporting} config={config} isOpenTrigger={isOpen} aluminumColor={aluminumColor} glassColor={glassColor} glassColor2={glassColor2} />;
    if ([15].includes(cat)) return <Fence3D isExporting={isExporting} config={config} aluminumColor={aluminumColor} glassColor={glassColor} glassColor2={glassColor2} />;
    if ([20].includes(cat)) {
      return (
        <GlassRoof3D
          isExporting={isExporting} config={config} aluminumColor={aluminumColor} glassColor={glassColor} glassColor2={glassColor2}
          position={[0, 0, 0]}
        />
      );
    }

    return null;
  };

  if (isResolvingTinyUrl) return <LoadingScreen message="Yönlendiriliyor..." />;
  if (decryptError) return <ErrorScreen message={decryptError} />;
  if (error) return <ErrorScreen message={error} />;
  if (!params || !config) return <LoadingScreen message="Yükleniyor..." />;

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>

      {/* Sol Panel */}
      <LeftPanel
        aluminumColor={aluminumColor}
        setAluminumColor={setAluminumColor}
        glassColor={glassColor}
        setGlassColor={setGlassColor}
        glassColor2={glassColor2}
        setGlassColor2={setGlassColor2}
        isDouble={config.isDouble}
        isOpen={isOpen}
        handleToggle={handleToggle}
        controlsRef={controlsRef}
        rendererRef={rendererRef}
        sceneRef={sceneRef}
        cameraRef={cameraRef}
        isMobile={isMobile}
        isEditMode={isEditMode}
        params={params}
        config={config}
      />

      {isMobile && config.is_ar_support && (
        <button
          onClick={handleARClick}
          style={{
            position: "absolute",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "10px 20px",
            fontSize: "16px",
            fontWeight: "500",
            borderRadius: "999px",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            color: "#81153F",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <img src="/3d.png" width="20" height="20" />
          Evinizde Deneyin
        </button>
      )}

      {isARLoading && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }}>
          <div style={{ fontSize: 24 }}>Yükleniyor...</div>
        </div>
      )}

      {arUrls.glb && arUrls.usdz && (
        <ARModelViewer
          glbUrl={arUrls.glb}
          usdzUrl={arUrls.usdz}
          modelViewerRef={modelViewerRef}
        />
      )}

      {/* 3D Sahne */}
      <div style={{ flex: 1, position: "relative" }}>
        <Canvas
          shadows
          camera={{ position: [5, 3, 5], fov: 50 }}
          onCreated={({ gl, scene, camera }) => {
            rendererRef.current = gl;
            sceneRef.current = scene;
            cameraRef.current = camera;
          }}>
          <ambientLight intensity={0.5} />
          <directionalLight
            castShadow
            position={[-20, 10, 8]}
            intensity={2}
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
            shadow-camera-near={0.5}
            shadow-camera-far={50}
            shadow-camera-left={10}
            shadow-camera-right={10}
            shadow-camera-top={10}
            shadow-camera-bottom={10}
          />
          <group ref={modelRef}>
            {renderSystem()}
          </group>
          <OrbitControls
            ref={controlsRef}
            enableZoom
            zoomSpeed={1}
            rotateSpeed={0.6}
            enableDamping
            dampingFactor={0.1}
            minPolarAngle={THREE.MathUtils.degToRad(0)}
            maxPolarAngle={THREE.MathUtils.degToRad(90)}
            onStart={() => {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
              setIsUserInteracting(true);
            }}
            onEnd={() => {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
              timeoutRef.current = setTimeout(() => {
                setIsUserInteracting(false);
              }, 3000); // 3 saniye sonra tekrar dönmeye başla
            }}
          />
        </Canvas>

        {!isEditMode && <CompanyLogo params={params} isMobile={isMobile} />}
        {!isEditMode && <DealerLogo params={params} isMobile={isMobile} userId={config.userId} />}
      </div>
    </div >
  );
}
